import { useContext, useCallback } from 'react';
import { modalContext } from '@/context/modal.context';
import { toastContext } from '@/context/toast.context';
import Collection from '@/utils/api/odas/collection';
import { isSuccessResponse, STANDARD_ERROR_MESSAGE } from '@/constants/httpStatus';
import { FETCHABLE_TYPE_MAP } from '@/constants/constants';
import t from '@/utils/Language/UI-translations';

/**
 * Custom hook to remove an item from a collection or collection category
 * @param {Object} options
 * @param {Object} options.collection - The parent collection
 * @param {Function} options.updateCollection - Callback to refresh after removal
 * @param {Object} [options.pageRef] - Optional ref to scroll into view after removal
 * @param {number} [options.categoryId] - If provided, removes from the category instead of the collection
 * @returns {Function} - removeItemFromCollection(resource, resourceType)
 */
export const useRemoveItemFromCollection = ({ collection, updateCollection, pageRef, categoryId }) => {
  const { enqueueModal, closeModal } = useContext(modalContext);
  const { addToast } = useContext(toastContext);

  const removeItemFromCollection = useCallback(
    (resource, resourceType) => {
      const fetchableType = FETCHABLE_TYPE_MAP[resourceType];

      const handleError = () => {
        addToast({ type: 'error' });
      };

      enqueueModal({
        name: 'ConfirmationModal',
        title: t('Remove resource from collection?'),
        text: t('Are you sure you want to remove this resource from this collection?'),
        confirmButtonText: t('Remove resource'),
        declineButtonText: t('Cancel'),
        buttonOrder: ['decline', 'confirm'],
        onConfirm: () => {
          const request = categoryId
            ? Collection.removeFromCollectionCategory(resource?.id, fetchableType, collection?.id, categoryId)
            : Collection.removeFromCollection(resource?.id, fetchableType, collection?.id);

          request
            .then((resp) => {
              if (isSuccessResponse(resp.status)) {
                if (pageRef?.current?.getBoundingClientRect().top < 0) {
                  pageRef.current.scrollIntoView({ behavior: 'smooth' });
                }
                updateCollection();
                addToast({ message: t(`${fetchableType} removed.`), type: 'success' });
                closeModal();
              } else {
                handleError();
              }
            })
            .catch(() => {
              handleError();
              closeModal();
            });
        },
        onError: () => {
          handleError();
          closeModal();
        }
      });
    },
    [collection, updateCollection, pageRef, categoryId, enqueueModal, closeModal, addToast]
  );

  return removeItemFromCollection;
};

export default useRemoveItemFromCollection;
